if (!customElements.get('share-button')) {
  customElements.define(
    'share-button',
    class ShareButton extends DetailsDisclosure {
      constructor() {
        super();

        this.elements = {
          shareButton: this.querySelector('[data-native-share]'),
          shareSummary: this.querySelector('summary'),
          copyButton: this.querySelector('.share-button__copy'),
          closeButton: this.querySelector('.share-button__close'),
          successMessage: this.querySelector('[role="status"]'),
          urlInput: this.querySelector('input'),
        };
        this.urlToShare = this.elements.urlInput?.value || document.location.href;
        this.copyGeneration = 0;
        this.shareGeneration = 0;
        this.mainDetailsToggle.addEventListener('toggle', this.toggleDetails.bind(this));
        this.elements.copyButton.addEventListener('click', this.copyToClipboard.bind(this));
        this.elements.closeButton.addEventListener('click', () => {
          const returnFocus = this.mainDetailsToggle.contains(document.activeElement) ||
            document.activeElement === document.body;
          this.close();
          if (returnFocus) this.elements.shareSummary.focus();
        });
        this.elements.urlInput.addEventListener('click', () => this.elements.urlInput.select());
        this.elements.urlInput.addEventListener('focus', () => this.elements.urlInput.select());
        this.mainDetailsToggle.addEventListener('keydown', (event) => {
          if (event.key !== 'Escape' || !this.mainDetailsToggle.open) return;
          event.preventDefault();
          event.stopPropagation();
          const returnFocus = this.mainDetailsToggle.contains(document.activeElement);
          this.close();
          if (returnFocus) this.elements.shareSummary.focus();
        });

        if (typeof navigator.share === 'function') {
          this.elements.shareButton.classList.remove('hidden');
          this.elements.shareButton.addEventListener('click', this.share.bind(this));
        }
      }

      disconnectedCallback() {
        this.cancelCopy();
        this.setMessage('');
        this.shareGeneration += 1;
      }

      ownsFocus(initiator) {
        return document.activeElement === initiator || document.activeElement === document.body;
      }

      setMessage(message) {
        this.elements.successMessage.textContent = message;
        this.elements.successMessage.classList.toggle('hidden', !message);
      }

      openFallback(message, initiator) {
        if (!this.isConnected) return;
        this.mainDetailsToggle.open = true;
        this.elements.shareSummary.setAttribute('aria-expanded', 'true');
        this.setMessage(message);
        if (this.ownsFocus(initiator)) {
          this.elements.urlInput.focus();
          this.elements.urlInput.select();
        }
      }

      async share() {
        if (this.nativeSharePending || !this.isConnected) return false;
        const generation = ++this.shareGeneration;
        const initiator = this.elements.shareButton;
        this.nativeSharePending = true;
        initiator.setAttribute('aria-disabled', 'true');
        try {
          await navigator.share({ url: this.urlToShare, title: document.title });
          if (this.isConnected && generation === this.shareGeneration && this.ownsFocus(initiator)) initiator.focus();
          return true;
        } catch (error) {
          if (this.isConnected && generation === this.shareGeneration) {
            this.openFallback(
              error?.name === 'AbortError' ? this.dataset.shareCancelled : this.dataset.shareFailed,
              initiator
            );
          }
          return false;
        } finally {
          this.nativeSharePending = false;
          initiator.removeAttribute('aria-disabled');
        }
      }

      toggleDetails() {
        this.elements.shareSummary.setAttribute('aria-expanded', String(this.mainDetailsToggle.open));
        if (!this.mainDetailsToggle.open) {
          this.cancelCopy();
          this.shareGeneration += 1;
          this.setMessage('');
        }
      }

      close() {
        super.close();
        this.cancelCopy();
        this.shareGeneration += 1;
        this.setMessage('');
      }

      cancelCopy() {
        this.copyGeneration += 1;
        this.settleCopy?.(false);
        this.settleCopy = null;
        this.elements.copyButton.removeAttribute('aria-disabled');
        this.elements.copyButton.removeAttribute('aria-busy');
      }

      async copyToClipboard() {
        if (this.settleCopy || !this.isConnected) return false;
        this.shareGeneration += 1;
        const generation = ++this.copyGeneration;
        const initiator = this.elements.copyButton;
        const url = this.elements.urlInput.value;
        if (typeof navigator.clipboard?.writeText !== 'function') {
          this.openFallback(this.dataset.copyManual, initiator);
          return false;
        }

        initiator.setAttribute('aria-disabled', 'true');
        initiator.setAttribute('aria-busy', 'true');
        this.setMessage(this.dataset.copyPending);
        const copied = await new Promise((resolve) => {
          let settled = false;
          const finish = (success) => {
            if (settled) return;
            settled = true;
            clearTimeout(timer);
            resolve(success);
          };
          const timer = setTimeout(() => finish(false), 5000);
          this.settleCopy = finish;
          try {
            Promise.resolve(navigator.clipboard.writeText(url)).then(() => finish(true), () => finish(false));
          } catch {
            finish(false);
          }
        });
        if (!this.isConnected || generation !== this.copyGeneration) return false;
        this.settleCopy = null;
        initiator.removeAttribute('aria-disabled');
        initiator.removeAttribute('aria-busy');
        this.setMessage(copied ? this.dataset.copySuccess : this.dataset.copyManual);
        if (!copied && this.ownsFocus(initiator)) {
          this.elements.urlInput.focus();
          this.elements.urlInput.select();
        }
        return copied;
      }

      updateUrl(url) {
        this.cancelCopy();
        this.shareGeneration += 1;
        this.urlToShare = url;
        this.elements.urlInput.value = url;
        this.setMessage('');
      }
    }
  );
}
